<script setup>
  import { ref, computed, watchEffect } from 'vue';

  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from '@/stores/UserStore';

  import BaseModal from './base/BaseModal.vue';
  import ModalContentTextInput from '@/components/ModalContentTextInput.vue';
  import ModalSaveContainerToProfileStep2 from '@/components/ModalSaveContainerToProfileStep2.vue';
  import { findMatchingProfileContainer } from '@/helpers/findMatchingProfileContainer';
  
  const showModal = defineModel('showModal', { default: true });

  const props = defineProps({
    containerTypeId: {
      type: String,
      required: true
    },
    containerTypeName: {
      type: String,
      required: true
    },
    defaultName: {
      type: String,
      required: false
    },
    contextName: {
      type: String,
      required: false
    },
    settings: {
      type: Object,
      required: true
    }
  });

  const spotStore = useSpotStore();
  const userStore = useUserStore();

  const defaultName = props.defaultName || `${spotStore.spotName} - ${props.containerTypeName}`;
  const saveContainerStep = ref(1);
  const savedContainerName = ref(defaultName);
  const nameInitialized = ref(false);

  const showStep = computed(() => (step) => saveContainerStep.value === step);
  const savedProfileContainers = computed(() => userStore.userProfiles?.profiles?.map(profile => (
    findMatchingProfileContainer(profile, props.containerTypeId, props.settings)
  )).filter(Boolean) || []);
  const savedProfileCount = computed(() => savedProfileContainers.value.length);
  const savedProfileMessage = computed(() => {
    if (savedProfileCount.value === 0) return null;

    return `Container currently saved in ${savedProfileCount.value} profile${savedProfileCount.value === 1 ? '' : 's'}.`;
  });

  function initializeSavedContainerName() {
    if (savedProfileCount.value === 0) return;

    const savedNames = new Set(savedProfileContainers.value.map(container => container.container_name));

    savedContainerName.value = savedNames.size === 1
      ? savedProfileContainers.value[0].container_name
      : `${defaultName} (${savedProfileCount.value + 1})`;
  }

  function closeModal() {
    showModal.value = false;
    saveContainerStep.value = 1;
  }

  async function handleSaveStep1() {
    if (!userStore.userProfiles) {
      await userStore.getProfiles();
    }

    if (!userStore.userProfiles?.profiles) return;

    if (userStore.userProfiles.profiles.length === 1) {
      const profile = userStore.userProfiles.profiles[0];
      const savedContainer = findMatchingProfileContainer(profile, props.containerTypeId, props.settings);

      if (!savedContainer) {
        await userStore.addContainerToProfile(profile.profile_id, props.containerTypeId, savedContainerName.value, props.settings);
        await userStore.getProfiles(true);
      }
    }

    saveContainerStep.value = 2;
  }

  function handleBackToName() {
    saveContainerStep.value = 1;
  }

  watchEffect(() => {
    if (!showModal.value) {
      saveContainerStep.value = 1;
      savedContainerName.value = defaultName;
      nameInitialized.value = false;
      return;
    }

    if (nameInitialized.value || !userStore.userProfiles?.profiles) return;

    initializeSavedContainerName();
    nameInitialized.value = true;
  });

</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="false"
  >

    <ModalContentTextInput
      v-if="showStep(1)"
      v-model="savedContainerName"
      :modalTitle="`Save ${props.containerTypeName}`"
      :subText="savedProfileMessage"
      acceptButtonText="Continue"
      @accept="handleSaveStep1"
      @cancel="closeModal"
    />

    <ModalSaveContainerToProfileStep2
      v-if="showStep(2)"
      v-model:savedContainerName="savedContainerName"
      :closeModal="closeModal"
      :containerTypeId="props.containerTypeId"
      :containerTypeName="props.containerTypeName"
      :contextName="props.contextName"
      :settings="props.settings"
      @back="handleBackToName"
    />  
  </BaseModal>
</template>
